<template>
  <form @submit.prevent="handleSubmit">
    <div class="profile-picture">
         <!-- Image Preview -->
          <template v-if="croppedImagePreviewUrl">
            <img :src="croppedImagePreviewUrl" class="preview-image">
          </template>
          <template v-else>
            <img :src="profilePlaceholder" alt="Profile">
          </template>
        <label for="imageUpload" class="file-label">&#9998;</label>
        <input type="file" id="imageUpload" accept="image/*" @change="previewImage">
        <ImageCropper :dialog="showImageCropper" :imgSrc="imagePreviewUrl" @handleCroppedImage="handleCroppedImage"/>
    </div>
    <div class="profile-error">
      <div class="field-error" v-if="errors.imageRaw">{{ errors.imageRaw }}</div>
    </div>
    <div class="input-group">
        <label for="fullName">{{ $t('screener.chatroom.register.form.file.fullname.label') }}</label>
        <input type="text" 
          :class="{ 'error-field': errors.name }" 
          id="fullName" 
          :placeholder="$t('screener.chatroom.register.form.file.fullname.label')"
          v-model="member.name" 
          @blur="validateFields('name')">
        <div class="field-error" v-if="errors.name">{{ errors.name }}</div>
    </div>
    <div class="input-group">
        <label for="bio">{{ $t('screener.chatroom.register.form.file.bio.label') }}</label>
        <textarea id="bio" 
          :class="{ 'error-field': errors.bio }" 
          :placeholder="$t('screener.chatroom.register.form.file.bio.label')" 
          rows="3"
          v-model="member.bio" 
          @blur="validateFields('bio')"></textarea>
        <div class="field-error" v-if="errors.bio">{{ errors.bio }}</div>
    </div>
    <div class="input-group">
      <label for="bio">{{ $t('screener.chatroom.user.create.want_know_more_label') }} <a href="javascript:void(0)" @click="$emit('handleBeInInfo')">{{ $t('screener.chatroom.user.create.click_know_more_label') }}</a></label>
    </div>
    <button type="submit" class="submit-btn" :class="(isLoading) ? 'submit-btn-loading' : null" :disabled="isLoading">
      <template v-if="isLoading">
        {{ $t('screener.chatroom.registering.loader.message') }}
      </template>
      <template v-else>
        {{ $t('screener.chatroom.register.form.submit.button') }}
      </template>
    </button>
    <AlertMessage :dialog="alertDialog"/>
  </form>
</template>

<script>

import AlertMessage from "../../dialogBox/AlertMessage.vue";
import { firebaseDatabase } from "@/mixins/firebase-database.js";
import ChatRoomService from "@/services/ChatRoomService";
import ImageCropper from '@/components/chatRoom/ImageCropper.vue' 
import profilePlaceholder from '@/assets/placeholder.jpg';

export default {
  name: 'RegisterForm',
  props: {
    consumer: {
      type: Object,
      default: null,
    },
    screener: {
      type: Object,
      default: null,
    }
  },
  components: {
    AlertMessage,
    ImageCropper
  },
  data() {
    return {
      isLoading:false,
      imagePreviewUrl: null,
      member: {
          name:null,
          email:null,
          bio: null,
          imageRaw:null,
          profile_image: null
      },
      errors: {
        name: '',
        bio: '',
        profile_image: '',
      },
      rules: {
        name: [
          v => !!v || 'Full Name is required',
          v => v && v.length >= 5 || 'Full Name must be greater than 5 characters',
          v => v && v.length <= 30 || 'Full Name must be less than 30 characters',
        ],
        bio: [
          v => !!v || 'Bio is required',
          v => v && v.length <= this.DEFINES.CHATROOM_MEMBERS.validation.bio || `Bio must not exceed ${this.DEFINES.CHATROOM_MEMBERS.validation.bio} characters`,
          v => v && v.length >= 10 || 'Bio must be at least 10 characters'
        ],
        imageRaw: [
          value => !!value || 'Profile Image is required',
          value => !value || value.size < 2000000 || 'Profile image size should be less than 2 MB!',
        ]
      },
      alertDialog: {
        show: false,
        text: "",
        color: ""
      },
      showImageCropper:false,
      croppedImagePreviewUrl:null,
      profilePlaceholder
    }
  },
  mixins: [firebaseDatabase],
  methods: {
    validateFields(field) {
      const value = this.member[field];
      const fieldRules = this.rules[field];

      // Check if fieldRules exists and is iterable
      if (!fieldRules || !Array.isArray(fieldRules)) {
        this.errors[field] = ''; // Clear any previous error if no rules exist
        return true; // Consider valid if no rules are defined
      }

      for (const rule of fieldRules) {
        const error = rule(value);
        if (error !== true) {
          this.errors[field] = error;
          return false; // Exit if validation fails
        }
      }

      this.errors[field] = ''; // Clear error if all rules pass
      return true; // Return success
    },
    validateForm() {
      let isValid = true;

      Object.keys(this.member).forEach(field => {
        
        if (!['imageRaw','profile_image'].includes(field)) {
          console.log(field);
          if (!this.validateFields(field)) {
            isValid = false; // Mark as invalid if any field fails
          }
        }
      });

      return isValid; // Return the overall form validity
    },
    previewImage(event) {
      const file = event.target.files[0]
      this.member.imageRaw = file
      if (file) {
        // Create a URL for the image
        this.imagePreviewUrl = URL.createObjectURL(file)

        // Optional: Clean up the URL when component is destroyed
        this.$once('hook:beforeDestroy', () => {
          URL.revokeObjectURL(this.imagePreviewUrl)
        })
      }

      const reader = new FileReader()
      
      reader.onloadend = () => {
        this.member.profile_image = reader.result
      }
      
      reader.readAsDataURL(file)
    },
    resetFormFields() {
      this.imagePreviewUrl = null;
      this.member = {
        name:null,
        email:null,
        bio: null,
        imageRaw:null,
        profile_image: null
      }
    },
    async handleSubmit() {
      try {
        let self = this
        if(!this.validateForm()){
          return;
        }
        this.member.email = this.consumer.email;
        this.member.chat_room_id = this.screener.chat_room.id;
        this.isLoading = true
        var response = await this.$store.dispatch("chatroom/registerMember", this.member);
        this.isLoading = false
        await ChatRoomService.__setMemberOnline(this.screener.chat_room.chat_room_id, response.member.id);
        this.resetFormFields();
        this.alertDialog = {
          show: true,
          text: response.message,
          color: "success"
        }
        this.$emit('handleprofileRegistered',response.member)
        // console.log(response.message);
      } catch (error) {
        console.log(error);
        this.isLoading = false
      }
    },
    handleCroppedImage(image) {
      this.member.profile_image = image
      this.croppedImagePreviewUrl = image
      this.showImageCropper = false
    }
  }
}
</script>

<style scoped>
 .error-field {
    border-color: red !important;
  }
  .field-error {
    color: red;
    margin-top: 5px;
  }
</style>